import { Font, View } from '@react-pdf/renderer';
import { BUSINESS_PLAN_TYPES, IBusinessToolType } from 'constants/businessPlan';
import { PDFPage } from 'PDF/components/Page';
import { ITextItem, PdfContentText } from 'PDF/components/PdfContentText';
import { SalesPlanDetailsTable } from 'PDF/components/SalesPlanDetailsTable/SalesPlanDetailsTable';
import { SubChapterHeading } from 'PDF/components/SubChapterHeading';
import { PDF_SECTIONS } from 'PDF/constants/sections';
import { Noto } from 'PDF/fonts';
import { hasDataToRender } from 'PDF/helpers/hasDataToRender';
import { prepareSectionId } from 'PDF/helpers/pdfMapping';
import { getSectionInfo } from 'PDF/helpers/sections';
import { ChartImagePdf } from 'PDF/pages/MarketingAndSales/ChartImagePdf';
import { SalesChannels } from 'PDF/pages/MarketingAndSales/SalesChannels/SalesChannels';
import { SalesFunnelImagePdf } from 'PDF/pages/MarketingAndSales/SalesFunnelImagePdf';
import { ISalesChannelsItem, ISalesPlanTableProps, IStageItem } from 'PDF/pages/MarketingAndSales/types';
import { SectionCoverPagePdf } from 'PDF/pages/SectionCoverPage';
import {
  getMarketingAndSalesChapterNames,
  MarketingAndSalesKeys,
  MarketingAndSalesSectionAvailability,
} from 'PDF/types/pdfSections';

import { getSubSectionHeading } from 'pages/BusinessToolPage/PdfPreview/utils';

import { getNextSectionIndex } from '../TableOfContents/helpers';

Font.getRegisteredFonts();
Font.register(Noto);

export interface IMarketingAndSalesData {
  firstMonetaryChartImageSrc: string;
  secondMonetaryChartImageSrc: string;
  monetaryRatioChartImageSrc: string;
  monetaryTotalValuesChartImageSrc: string;
  physicalValuesChartImageSrc: string;
  salesFunnelImageSrc: string;
  salesFunnelData: IStageItem[];
  salesChannelsData: ISalesChannelsItem[];
  salesPlanTableData: ISalesPlanTableProps;
}

export interface IMarketingAndSalesPdfProps {
  magazineItems: ITextItem[][];
  data: IMarketingAndSalesData;
  businessPlanType: IBusinessToolType;
  sectionPicture?: string;
  isStandardOrLightTariff: boolean;
  isExamplePage: boolean;
}

export const MarketingAndSalesPdf = (
  props: IMarketingAndSalesPdfProps & {
    availability: MarketingAndSalesSectionAvailability;
    isPreviewMode?: boolean;
    sectionNumber: string;
  },
) => {
  const {
    magazineItems,
    data,
    businessPlanType,
    sectionPicture,
    availability,
    isPreviewMode = false,
    sectionNumber,
    isStandardOrLightTariff,
    isExamplePage,
  } = props;
  const { sectionHeading } = getSectionInfo(businessPlanType, PDF_SECTIONS.MARKETING_AND_SALES, isExamplePage);
  const getSubHeading = getSubSectionHeading(sectionNumber);
  const getSubChapterIndex = getNextSectionIndex();
  const MARKETING_AND_SALES_CHAPTER_NAMES = getMarketingAndSalesChapterNames(isExamplePage);
  const isInvestor = businessPlanType === BUSINESS_PLAN_TYPES.FOR_INVESTORS;
  const subChapterNames = (Object.keys(availability) as MarketingAndSalesKeys[])
    .filter((key) => !!availability[key])
    .map((key) => `${Number(sectionNumber)}.${getSubChapterIndex()}. ${MARKETING_AND_SALES_CHAPTER_NAMES[key]}`);
  const isSectionEmpty = Object.values(availability).every((value) => !value);
  const isCoverPageEmpty = isSectionEmpty && isPreviewMode;

  if (!data) {
    return (
      <SectionCoverPagePdf
        header={sectionHeading}
        sectionNumber={sectionNumber}
        sectionId={prepareSectionId(businessPlanType, PDF_SECTIONS.MARKETING_AND_SALES)}
        background={sectionPicture ?? ''}
        highlightSettings={{ position: 0, variant: 'before' }}
        subChapterNames={subChapterNames}
        isEmpty={isCoverPageEmpty}
        isStandardOrLightTariff={isStandardOrLightTariff}
        isExamplePage={isExamplePage}
      />
    );
  }

  const {
    firstMonetaryChartImageSrc,
    secondMonetaryChartImageSrc,
    monetaryRatioChartImageSrc,
    monetaryTotalValuesChartImageSrc,
    salesFunnelImageSrc,
    salesFunnelData,
    salesChannelsData,
    salesPlanTableData,
  } = data;

  const isSalesPlanTableNotEmpty = salesPlanTableData.rows.length > 0;

  return (
    <>
      {(!isSectionEmpty || isCoverPageEmpty) && (
        <SectionCoverPagePdf
          header={sectionHeading}
          sectionNumber={sectionNumber}
          sectionId={prepareSectionId(businessPlanType, PDF_SECTIONS.MARKETING_AND_SALES)}
          background={sectionPicture ?? ''}
          highlightSettings={{ position: 0, variant: 'before' }}
          subChapterNames={subChapterNames}
          isEmpty={isCoverPageEmpty}
          isStandardOrLightTariff={isStandardOrLightTariff}
          isExamplePage={isExamplePage}
        />
      )}
      {!isSectionEmpty && (
        <PDFPage
          shouldShowHeaderAndFooter
          sectionNumber={sectionNumber}
          sectionTitle={sectionHeading}
          withEmptySpaceImage
          isStandardOrLightTariff={isStandardOrLightTariff}
        >
          {availability.salesPlan && (
            <>
              <SubChapterHeading heading={getSubHeading(MARKETING_AND_SALES_CHAPTER_NAMES.salesPlan)} id="salesPlan" />
              {!isInvestor && (
                <>
                  {hasDataToRender(salesFunnelData) ? (
                    <SalesFunnelImagePdf
                      imageSrc={salesFunnelImageSrc}
                      itemsNumber={salesFunnelData.length}
                      isExamplePage={isExamplePage}
                    />
                  ) : (
                    <View></View>
                  )}
                  <PdfContentText items={magazineItems[0]} withoutBottomMargin />
                  <PdfContentText items={magazineItems[1]} withoutBottomMargin={!isSalesPlanTableNotEmpty} />
                </>
              )}
              {isInvestor && (
                <PdfContentText items={magazineItems[0]} withoutBottomMargin={!isSalesPlanTableNotEmpty} />
              )}
              {isSalesPlanTableNotEmpty && (
                <SalesPlanDetailsTable
                  columns={salesPlanTableData.columns}
                  rows={salesPlanTableData.rows}
                  totalValues={salesPlanTableData.totalValues}
                  currencySymbol={salesPlanTableData.currencySymbol}
                  unit={salesPlanTableData.unit}
                  isExamplePage={isExamplePage}
                />
              )}
              <ChartImagePdf imageSrc={firstMonetaryChartImageSrc} />
              <ChartImagePdf imageSrc={secondMonetaryChartImageSrc} />

              {/* physicalValuesChartImageSrc is temporary hidden because of client's wish  */}
              {/* this graph does not take into account the different dimensions of the product units  */}
              {/* in the future it is planned to build this graph for each dimension separately  */}
              {/* <ChartImagePdf imageSrc={physicalValuesChartImageSrc} /> */}
              <ChartImagePdf imageSrc={monetaryRatioChartImageSrc} />

              {!!monetaryTotalValuesChartImageSrc && monetaryTotalValuesChartImageSrc !== 'emptyChart' && (
                <View style={{ marginBottom: 30 }}>
                  <ChartImagePdf imageSrc={monetaryTotalValuesChartImageSrc} />
                </View>
              )}
              <SalesChannels data={salesChannelsData} isExamplePage={isExamplePage} />
              <PdfContentText items={magazineItems[!isInvestor ? 2 : 1]} />
            </>
          )}
          {availability.marketingPlan && (
            <>
              <SubChapterHeading
                heading={getSubHeading(MARKETING_AND_SALES_CHAPTER_NAMES.marketingPlan)}
                id="marketingPlan"
              />
              <PdfContentText items={magazineItems[!isInvestor ? 3 : 2]} />
            </>
          )}
        </PDFPage>
      )}
    </>
  );
};
